import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ChangePasswordComponent } from '../../account-settings/change-password/change-password.component';
import { MfaStatus } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-admin-account-security',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NzButtonModule,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzSpinModule,
    ChangePasswordComponent,
  ],
  templateUrl: './admin-account-security.component.html',
  styleUrl: './admin-account-security.component.less',
})
export class AdminAccountSecurityComponent implements OnInit {
  mfaStatus: MfaStatus | null = null;
  isMfaStatusLoading = true;
  mfaStatusLoadFailed = false;
  isStartingReplacement = false;
  readonly replacementForm: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly message: NzMessageService,
    private readonly translate: TranslateService,
    private readonly router: Router,
  ) {
    this.replacementForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  ngOnInit(): void {
    this.authService.getMe().subscribe({
      next: (profile) => {
        this.mfaStatus = profile.mfaStatus ?? null;
        this.isMfaStatusLoading = false;
      },
      error: () => {
        this.mfaStatusLoadFailed = true;
        this.isMfaStatusLoading = false;
      },
    });
  }

  get canReplaceAuthenticator(): boolean {
    return this.mfaStatus?.enabled === true && this.mfaStatus.state === 'enabled';
  }

  startReplacement(): void {
    if (this.replacementForm.invalid) {
      this.replacementForm.markAllAsTouched();
      return;
    }

    this.isStartingReplacement = true;
    const code = this.replacementForm.get('code')?.value as string;
    this.authService.startMfaReplacement(code).subscribe({
      next: (response) => {
        this.isStartingReplacement = false;
        if (!response.mfaRequired || !response.mfaChallenge || response.mfaState !== 'replacement_enrollment') {
          this.message.error(this.translate.instant('ADMIN.ACCOUNT_SECURITY.REPLACEMENT_START_FAILED'));
          return;
        }

        this.authService.setPendingMfaChallenge(response);
        this.authService.clearAccessSession();
        this.router.navigate(['/login']);
      },
      error: () => {
        this.isStartingReplacement = false;
        this.message.error(this.translate.instant('ADMIN.ACCOUNT_SECURITY.REPLACEMENT_START_FAILED'));
      },
    });
  }
}
